import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import { daysBetween, formatDate, formatElapsedDays } from '@/lib/datetime';
import { getDayTime, listDayTimes } from '@/lib/schedule';

interface StudentSummaryHeaderProps {
  student: Student;
  onUpdated?: (student: Student) => void;
}

export default function StudentSummaryHeader({ student }: StudentSummaryHeaderProps) {
  const [progressStats, setProgressStats] = useState({ done: 0, total: 0 });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('progress_items')
          .select('status')
          .eq('student_id', student.id);
        if (!active) return;
        const rows = (data as { status: string }[]) ?? [];
        setProgressStats({
          done: rows.filter((r) => r.status === 'done').length,
          total: rows.length,
        });
      } catch {
        if (active) setProgressStats({ done: 0, total: 0 });
      }
    })();
    return () => {
      active = false;
    };
  }, [student.id]);

  const progressPercentValue =
    progressStats.total === 0
      ? 0
      : Math.round((progressStats.done / progressStats.total) * 100);

  const isEnded = student.status === 'ended';

  const initial = (student.name || '?').trim().charAt(0).toUpperCase();
  const days = daysBetween(student.first_lesson_date);
  const elapsedLabel = formatElapsedDays(days);
  const dayCount = days !== null && days >= 0 ? days : 0;

  return (
    <div className="rounded-2xl border border-background-200 bg-gradient-to-br from-primary-50 to-background-100 p-6 md:p-8">
      {isEnded && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-user-unfollow-line"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-accent-900">수업이 종료된 학생이에요</p>
            <p className="mt-0.5 text-xs text-foreground-600">
              종료일 {formatDate(student.ended_at)} · 수업을 다시 시작하려면 페이지 맨 아래의 ‘수업
              재개’를 눌러주세요.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-500 text-2xl font-bold text-background-50">
            {initial}
          </span>
          <div>
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{student.name}</h1>
            <p className="mt-1 text-sm text-foreground-600">
              {student.school || '학교 미입력'} · {student.grade || '학년 미입력'}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {student.target_school && (
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                  <i className="ri-flag-2-line"></i>
                  목표 · {student.target_school}
                </span>
              )}
              <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-700">
                <i className="ri-graduation-cap-line"></i>
                {student.target_type || '고등학교'}
              </span>
              {student.desired_major && (
                <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                  <i className="ri-award-line"></i>
                  희망 학과 · {student.desired_major}
                </span>
              )}
              {student.desired_career && (
                <span className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
                  <i className="ri-compass-3-line"></i>
                  희망 진로 · {student.desired_career}
                </span>
              )}
              {listDayTimes(student, student.class_days ?? []).map((t) => (
                <span
                  key={t.day}
                  className="flex items-center gap-1.5 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-800"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-500 text-[11px] font-bold text-background-50">
                    {t.day}
                  </span>
                  {t.start ? (
                    <span>
                      {t.start}
                      {t.end ? `~${t.end}` : ''}
                    </span>
                  ) : (
                    <span className="text-foreground-500">시간 미입력</span>
                  )}
                </span>
              ))}
              {(student.class_days ?? []).length === 0 && getDayTime(student, '').start && (
                <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-700">
                  <i className="ri-time-line"></i>
                  {getDayTime(student, '').start} ~ {getDayTime(student, '').end}
                </span>
              )}
            </div>
            {(student.parent_contact || student.lesson_location) && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-foreground-600">
                {student.parent_contact && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-parent-line text-secondary-600"></i>
                    <span className="text-foreground-500">학부모 연락처</span>
                    <span className="font-semibold text-foreground-800">{student.parent_contact}</span>
                  </span>
                )}
                {student.lesson_location && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-2-line text-primary-600"></i>
                    <span className="text-foreground-500">교습 장소</span>
                    <span className="font-semibold text-foreground-800">{student.lesson_location}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:w-auto">
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-route-line"></i>
              진도 달성률
            </p>
            <p className="mt-1 text-xl font-bold text-foreground-900">{progressPercentValue}%</p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
              <div
                className="h-full rounded-full bg-primary-500 transition-all"
                style={{ width: `${progressPercentValue}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-foreground-500">
              완료 {progressStats.done} / 전체 {progressStats.total}
            </p>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-seedling-line"></i>
              함께한 기간
            </p>
            <p className="mt-1 text-xl font-bold text-primary-700">{elapsedLabel}</p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
              <div
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${Math.min(100, (dayCount % 30) * 3.3)}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-foreground-500">총 {dayCount}일</p>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-calendar-check-line"></i>
              첫 수업일
            </p>
            <p className="mt-1 text-base font-bold text-foreground-900">
              {formatDate(student.first_lesson_date)}
            </p>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-flag-line"></i>
              목표 진도일
            </p>
            <p className="mt-1 text-base font-bold text-foreground-900">
              {formatDate(student.goal_deadline)}
            </p>
          </div>
        </div>
      </div>

      {student.goal_progress && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-flag-2-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-accent-800">목표 진도</p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-800">
              {student.goal_progress}
            </p>
            {student.goal_deadline && (
              <p className="mt-1 text-xs text-foreground-500">
                목표 달성일 · {formatDate(student.goal_deadline)}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-4 border-t border-background-300/60 pt-5 text-xs text-foreground-600">
        <span>
          <span className="font-semibold text-foreground-800">개념 </span>
          {student.concept_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">연산 </span>
          {student.calc_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">유형 </span>
          {student.type_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">심화 </span>
          {student.advanced_book || '-'}
        </span>
        {student.completed_books && (
          <span className="flex items-start gap-1">
            <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
            <span>
              <span className="font-semibold text-foreground-800">한번 다 푼 교재 </span>
              <span className="whitespace-pre-wrap">{student.completed_books}</span>
            </span>
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/students"
          className="inline-flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-800"
        >
          <i className="ri-arrow-left-line"></i>
          학생 목록으로
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/students/${student.id}/edit`}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            학생 정보 수정
          </Link>
        </div>
      </div>
    </div>
  );
}